'use client'

import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { cn } from '@segway/core'

import { AddIcon, MinusIcon } from '@/components/icons'

interface StepperProps {
  value?: number
  defaultValue?: number
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  incrementDisabled?: boolean
  onChange?: (newValue: number, oldValue: number) => void
  handleScrollToItem?: () => void
  containerClass?: string
  inputClass?: string
  inputLeftClass?: string
  inputRightClass?: string
  iconLeftWidth?: number
  iconLeftHeight?: number
  iconRightWidth?: number
  iconRightHeight?: number
}

interface StepperRef {
  focus: () => void
  blur: () => void
}

/**
 * Stepper 组件
 *
 * @param {StepperProps} props - 组件的属性
 * @param {number} [props.value] - 当前数量值（受控模式，非必填）
 * @param {number} [props.defaultValue=0] - 初始数量值（非受控模式，默认为 0）
 * @param {number} [props.min=0] - 最小值，默认为 0
 * @param {number} [props.max=99] - 最大值，默认为 9999
 * @param {number} [props.step=1] - 每次增加或减少的步长，默认为 1
 * @param {boolean} [props.disabled=false] - 是否禁用，默认为 false
 * @param {boolean} [props.incrementDisabled=false] - 增加是否禁用，默认为 false
 * @param {function(number, number):void} [props.onChange] - 数值变化时的回调函数，第一个参数为新值，第二参数为旧值
 * @param {function():void} [props.handleScrollToItem] - 当输入框聚焦时触发的滚动回调
 * @param {React.Ref<StepperRef>} ref - 用于暴露组件的公共 API，例如 `focus` 和 `blur`
 *
 * @returns {JSX.Element} Stepper 组件
 */
const Stepper = forwardRef<StepperRef, StepperProps>(
  (
    {
      value,
      defaultValue = 1,
      min = 1,
      max = 9999,
      step = 1,
      disabled = false,
      onChange,
      handleScrollToItem,
      incrementDisabled = false,
      containerClass = '',
      inputClass = '',
      inputLeftClass = '',
      inputRightClass = '',
      iconLeftWidth = 18,
      iconLeftHeight = 2,
      iconRightWidth = 18,
      iconRightHeight = 18,
    },
    ref,
  ) => {
    const isControlled = value !== undefined // 判断是否为受控模式
    const [internalValue, setInternalValue] = useState<number>(defaultValue) // 内部状态
    const currentValue = isControlled ? value : internalValue // 当前值
    const [inputValue, setInputValue] = useState<string>(String(currentValue))
    const inputRef = useRef<HTMLInputElement>(null)

    /**
     * 是否不可减少
     */
    const isDecrementDisabled = useMemo(() => {
      return currentValue <= min || disabled
    }, [currentValue, min, disabled])

    /**
     * 是否不可增加
     */
    const isIncrementDisabled = useMemo(() => {
      return currentValue >= max || disabled || incrementDisabled
    }, [currentValue, max, disabled, incrementDisabled])

    /**
     * 更新值并触发回调
     */
    const handleChange = (newValue: number) => {
      const clampedValue = Math.max(min, Math.min(max, newValue))
      if (!isControlled) {
        setInternalValue(clampedValue) // 更新内部状态
        setInputValue(String(clampedValue)) // 同步更新输入框显示
      }
      if (onChange) {
        onChange(clampedValue, newValue) // 通知父组件
      }
    }

    /**
     * 减少事件
     */
    const handleDecrement = () => {
      if (isDecrementDisabled) {
        return
      }

      if (currentValue > min) {
        handleChange(currentValue - step)
      }
    }

    /**
     * 增加事件
     */
    const handleIncrement = () => {
      if (isIncrementDisabled) {
        return
      }
      if (currentValue < max) {
        handleChange(currentValue + step)
      }
    }

    /**
     * input 输入事件
     */
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value
      let correctValue = text.replace(/[^0-9]/g, '')

      if (correctValue.length > 1 && correctValue.startsWith('0')) {
        correctValue = correctValue.replace(/^0+/, '')
      }
      setInputValue(correctValue)
      const numericValue = parseInt(correctValue, 10)
      if (!isControlled && !isNaN(numericValue)) {
        handleChange(numericValue)
      }
    }

    /**
     * input 失去焦点事件
     */
    const handleInputBlur = () => {
      const numericValue = parseInt(inputValue, 10)

      if (isNaN(numericValue)) {
        // 如果输入框内容无效，恢复为当前值
        setInputValue(String(currentValue))
      } else {
        // 更新为合法的值
        handleChange(numericValue)
      }
    }

    /**
     * 同步受控模式的 value 到输入框
     */
    useEffect(() => {
      if (isControlled) {
        setInputValue(String(value))
      }
    }, [value, isControlled])

    /**
     * 暴露 Api
     */
    useImperativeHandle(ref, () => {
      return {
        focus: () => {
          inputRef.current?.focus()
        },
        blur: () => {
          inputRef.current?.blur()
        },
      }
    })

    return (
      <div className={cn('flex h-[36px] items-center xl:h-[46px]', containerClass)}>
        <button
          className={cn(
            'flex h-full w-[36px] items-center justify-center rounded-l-full border border-gray-3 bg-gray-1 xl:w-[46px]',
            inputLeftClass,
            {
              'cursor-not-allowed bg-white': isDecrementDisabled,
            },
          )}
          onClick={handleDecrement}
          disabled={isDecrementDisabled}>
          <MinusIcon
            width={iconLeftWidth}
            height={iconLeftHeight}
            color={isDecrementDisabled ? '#BBBBBD' : '#000000'}
          />
        </button>
        <input
          ref={inputRef}
          type="tel"
          className={cn(
            'h-full w-[54px] rounded-none border-x-0 border-y border-gray-3 text-center font-miSansMedium380 text-lg focus:outline-none xl:w-[67px] xl:text-2xl',
            inputClass,
          )}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleScrollToItem}
          disabled={disabled}
          maxLength={max.toString().length}
        />
        <button
          className={cn(
            'flex h-full w-[36px] items-center justify-center rounded-r-full border border-gray-3 bg-gray-1 xl:w-[46px]',
            inputRightClass,
            {
              'cursor-not-allowed bg-white': isIncrementDisabled,
            },
          )}
          onClick={handleIncrement}
          disabled={isIncrementDisabled}>
          <AddIcon
            width={iconRightWidth}
            height={iconRightHeight}
            color={isIncrementDisabled ? '#BBBBBD' : '#000000'}
          />
        </button>
      </div>
    )
  },
)

Stepper.displayName = 'Stepper'

export default Stepper
